import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Generic function to get all documents from a collection
 * @param {string} collectionName - Name of the Firestore collection
 * @returns {Promise<Array>} Array of documents with IDs
 */
export const getAllDocuments = async (collectionName) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    const documents = [];
    
    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, data: documents };
  } catch (error) {
    console.error(`Error fetching ${collectionName}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single document by ID
 * @param {string} collectionName - Name of the Firestore collection
 * @param {string} documentId - Document ID
 * @returns {Promise<Object>} Document data with ID
 */
export const getDocument = async (collectionName, documentId) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { 
        success: true, 
        data: { id: docSnap.id, ...docSnap.data() } 
      };
    } else {
      return { success: false, error: 'Document not found' };
    }
  } catch (error) {
    console.error(`Error fetching document:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Add a new document to a collection
 * @param {string} collectionName - Name of the Firestore collection
 * @param {Object} data - Document data
 * @returns {Promise<Object>} Created document with ID
 */
export const addDocument = async (collectionName, data) => {
  try {
    const docRef = await addDoc(collection(db, collectionName), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { 
      success: true, 
      data: { id: docRef.id, ...data } 
    };
  } catch (error) {
    console.error(`Error adding document:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing document
 * @param {string} collectionName - Name of the Firestore collection
 * @param {string} documentId - Document ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Result of update operation
 */
export const updateDocument = async (collectionName, documentId, data) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error(`Error updating document:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a document
 * @param {string} collectionName - Name of the Firestore collection
 * @param {string} documentId - Document ID
 * @returns {Promise<Object>} Result of delete operation
 */
export const deleteDocument = async (collectionName, documentId) => {
  try {
    await deleteDoc(doc(db, collectionName, documentId));
    return { success: true };
  } catch (error) {
    console.error(`Error deleting document:`, error);
    return { success: false, error: error.message };
  }
};


export const projectsService = {
  getAll: () => getAllDocuments('projects'),
  getById: (id) => getDocument('projects', id),
  create: (data) => addDocument('projects', data),
  update: (id, data) => updateDocument('projects', id, data),
  delete: (id) => deleteDocument('projects', id)
};

export const researchService = {
  getAll: () => getAllDocuments('research'),
  getById: (id) => getDocument('research', id),
  create: (data) => addDocument('research', data),
  update: (id, data) => updateDocument('research', id, data),
  delete: (id) => deleteDocument('research', id)
};


export const blogsService = {
  getAll: () => getAllDocuments('blogs'),
  getById: (id) => getDocument('blogs', id),
  create: (data) => addDocument('blogs', data),
  update: (id, data) => updateDocument('blogs', id, data),
  delete: (id) => deleteDocument('blogs', id)
};


export const experienceService = {
  getAll: () => getAllDocuments('experience'),
  getById: (id) => getDocument('experience', id),
  create: (data) => addDocument('experience', data),
  update: (id, data) => updateDocument('experience', id, data),
  delete: (id) => deleteDocument('experience', id)
};

export const educationService = {
  getAll: () => getAllDocuments('education'),
  getById: (id) => getDocument('education', id),
  create: (data) => addDocument('education', data),
  update: (id, data) => updateDocument('education', id, data),
  delete: (id) => deleteDocument('education', id)
};

export const profileService = {
  getAll: () => getAllDocuments('profile'),
  getById: (id) => getDocument('profile', id),
  create: (data) => addDocument('profile', data),
  update: (id, data) => updateDocument('profile', id, data),
  delete: (id) => deleteDocument('profile', id)
};

export const involvementsService = {
  getAll: () => getAllDocuments('involvements'),
  getById: (id) => getDocument('involvements', id),
  create: (data) => addDocument('involvements', data),
  update: (id, data) => updateDocument('involvements', id, data),
  delete: (id) => deleteDocument('involvements', id)
};

export const awardsService = {
  getAll: () => getAllDocuments('awards'),
  getById: (id) => getDocument('awards', id),
  create: (data) => addDocument('awards', data),
  update: (id, data) => updateDocument('awards', id, data),
  delete: (id) => deleteDocument('awards', id)
};














